import { charts } from '@ecosystem/newron-design-system';
import { isEmpty } from 'lodash';
import {
  BrandDependencyCategory,
  BrandDependencyChartData,
  BrandDependencyChartSubResp,
  BrandDependencyKpi,
  BrandDependencyKpiDisplayName,
  BrandDependencySort,
  ComponentName,
} from '@models/BrandDependency';
import {
  TRADING_ASSISTANT_BRAND_WITHIN_RETAILER_SUBTITLE,
  TRADING_ASSISTANT_BRAND_WITHIN_RETAILER_SUBTITLE_WITHIN,
  TRADING_ASSISTANT_RETAILER_WITHIN_BRAND_SUBTITLE,
  TRADING_ASSISTANT_RETAILER_WITHIN_BRAND_SUBTITLE_WITHIN,
} from '@pages/Utilities';
import { getAppliedFiltersFromPagesConfig, getInitialAppliedCountries } from '@utils/FilterUtils';
import { useInViewTracking } from '@utils/hook/tracking/UseInViewTracking';
import { ElementType } from '@utils/page-analytics/model/AnalyticTrackingBasePropertiesObj';
import { useGeneratePageElementViewParam } from '@utils/page-analytics/model/PageElementViewedTracking';
import { usePageConfig } from '@utils/pageConfig/PagesConfigContext';
import { FactRevenue, FeatureOption, FilterKeys, PageName, PageType, SubSection } from '@utils/pageConfig/Types';
import { renderVisualization } from '../utilities/ChartUtils';
import * as styles from './BrandDependencyChart.module.scss';
import {
  ensureButterflyDataPercentages,
  getBrandDependencyKpiCategory,
  getBrandLabel,
  getChartSubtitles,
  getMarketDataByKpi,
  getRetailerDataByKpi,
  getSortedBrandColorCodes,
  isBrandDependencyKpiRevenue,
  mapBrandDependencyData,
  mapY2Data,
  sortBrandDependencyData,
} from './BrandDependencyChartUtils';
import { GrowthTypes } from '@components/comparison-growth-selector/Types';
import { formatCurrencyPattern, formatKpi, Kpi } from '@utils/Formatters';
import { KpiResp } from '@models/CommonResp';
import { useMemo } from 'react';
import { CapturedImage } from '@ecosystem/newron-design-system/build/export-utils/captureImage';
import { ProductType } from '@pages/product-analyzer/Product';

interface Props {
  isLoading: boolean;
  chartData: BrandDependencyChartSubResp[];
  kpi: BrandDependencyKpi;
  retailerClientName?: string;
  sortBy?: BrandDependencySort;
  selectedGrowthType: GrowthTypes;
  factRevenue: FactRevenue;
  bySegment: FeatureOption;
  isDeepDiveView: boolean;
  viewingBrand?: string;
  brandsColorMap: { [key: string]: string };
  subSectionForAmplitudeTracking: SubSection;
  effectiveProductType: ProductType;
  onExport?: (img: CapturedImage) => void;
  isApiError?: boolean;
}

const BrandDependencyChart: React.FC<Props> = ({
  isLoading,
  chartData,
  kpi,
  retailerClientName = 'Retailer',
  sortBy = BrandDependencySort.retailerShareOfBrand,
  selectedGrowthType,
  factRevenue,
  bySegment,
  isDeepDiveView = false,
  viewingBrand = '',
  brandsColorMap,
  subSectionForAmplitudeTracking,
  effectiveProductType,
  onExport = () => {},
  isApiError = false,
}) => {
  const pagesConfig = usePageConfig();
  const productGroups = getAppliedFiltersFromPagesConfig(
    pagesConfig,
    FilterKeys.PRODUCT_GROUP,
    PageType.OPPORTUNITY_REVIEW,
  );
  const productGroup =
    productGroups && productGroups.length === 1
      ? productGroups[0]?.label ?? ""
      : "";

  const pageElementViewParam = useGeneratePageElementViewParam('mainFiltersMarketPremium');
  const customPageElementViewParam = () => ({
    ...pageElementViewParam,
    pageType: PageType.OPPORTUNITY_REVIEW,
    pageName: PageName.TRADING_ASSISTANT,
    componentName: ComponentName.RETAILER_MARKET_RELATIONSHIP_BUTTERFLY_CHART,
    elementType: ElementType.BUTTERFLY_CHART,
    hasData: !isEmpty(chartData),
    access: pagesConfig.access,
    initialLocationId: getInitialAppliedCountries(pagesConfig, pagesConfig.pageType, effectiveProductType),
    subSection: subSectionForAmplitudeTracking,
  });
  const { ref } = useInViewTracking(customPageElementViewParam, {
    threshold: 0.3,
    triggerOnce: true,
  });

  const category: BrandDependencyCategory = getBrandDependencyKpiCategory(kpi);

  const mappedData: BrandDependencyChartData[] = chartData
    .map(mapBrandDependencyData)
    .sort((d1, d2) => sortBrandDependencyData(d1, d2, sortBy, category, isDeepDiveView));

  const mappedDataWithDefaultValues = useMemo(() => ensureButterflyDataPercentages(mappedData), [mappedData]);

  const chartColorCodes = getSortedBrandColorCodes(isDeepDiveView, mappedData, brandsColorMap, viewingBrand);

  const { ButterflyChart } = charts;
  const currencySymbol = factRevenue?.currencySymbol ?? '';

  const tooltipValue = (kpiData: KpiResp) => {
    let kpiValue = formatKpi(Kpi.marketVolume, kpiData.value);
    if (isBrandDependencyKpiRevenue(kpi)) {
      kpiValue = formatKpi(Kpi.marketRevenue, kpiData.value, { currencySymbol });
    }
    return `${formatCurrencyPattern(kpiValue)} (${formatKpi(Kpi.revenueShare, kpiData.percentage)})`;
  };

  const chartKey = useMemo(
    () => `${bySegment.feature}-${kpi}-${selectedGrowthType}-${sortBy}-${viewingBrand}`,
    [bySegment.feature, kpi, selectedGrowthType, sortBy, viewingBrand],
  );

  return renderVisualization({
    isLoading,
    fetchedApiData: chartData,
    defaultData: [],
    jsxToRender: (
      <div ref={ref} className={styles.BrandDependencyChart}>
        <ButterflyChart
          key={chartKey}
          data={mappedDataWithDefaultValues}
          xLeft={(data: BrandDependencyChartData) => data.retailerWithinBrand[category].sharePercentage / 100}
          xRight={(data: BrandDependencyChartData) => data.brandWithinRetailer[category].sharePercentage / 100}
          y={(data: BrandDependencyChartData) => data.brand ?? data.featureValue ?? ''}
          y2={(data: BrandDependencyChartData) => mapY2Data(data, kpi, selectedGrowthType, currencySymbol)}
          xLeftLabels={(data: BrandDependencyChartData) => data.retailerWithinBrand[category].growthPercentage}
          xRightLabels={(data: BrandDependencyChartData) => data.brandWithinRetailer[category].growthPercentage}
          yAxisLabel={{
            label: isDeepDiveView ? bySegment.label : BrandDependencyKpiDisplayName[kpi],
          }}
          multicolor={!isDeepDiveView}
          leftChartTitle={`${retailerClientName} share of Brand ${category.charAt(0).toUpperCase()}${category.slice(
            1,
          )}`}
          leftChartSubtitle={getChartSubtitles(
            bySegment.label,
            TRADING_ASSISTANT_RETAILER_WITHIN_BRAND_SUBTITLE,
            TRADING_ASSISTANT_RETAILER_WITHIN_BRAND_SUBTITLE_WITHIN,
          )}
          rightChartTitle={`Brand share of ${retailerClientName} ${category.charAt(0).toUpperCase()}${category.slice(
            1,
          )}`}
          rightChartSubtitle={getChartSubtitles(
            bySegment.label,
            TRADING_ASSISTANT_BRAND_WITHIN_RETAILER_SUBTITLE,
            TRADING_ASSISTANT_BRAND_WITHIN_RETAILER_SUBTITLE_WITHIN,
          )}
          showTooltip
          tooltipOptions={{
            showHeader: true,
            customHeader: retailerClientName,
            ...(productGroup && { customSubHeader: productGroup }),
            values: [
              {
                label: (data: BrandDependencyChartData) => `${getBrandLabel(data, viewingBrand)} within Market`,
                value: (data: BrandDependencyChartData) => tooltipValue(data[getMarketDataByKpi(category)]),
              },
              {
                label: (data: BrandDependencyChartData) => `${getBrandLabel(data, viewingBrand)} within Retailer`,
                value: (data: BrandDependencyChartData) => tooltipValue(data[getRetailerDataByKpi(category)]),
              },
            ],
          }}
          {...(isDeepDiveView && {
            yAxisGroupLabel: viewingBrand,
          })}
          customChartColors={chartColorCodes}
          onExport={onExport}
        />
      </div>
    ),
    isError: isApiError,
  });
};

export default BrandDependencyChart;
